import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2
} from '@angular/core';
import { ApplicationConfig } from '../../config/config';

@Directive({
  selector: '[ngcInspireScroll]',
  exportAs: 'ngcInspireScroll'
})
export class InspireScrollDirective implements OnInit, OnDestroy {
  private stickyHeaderHeight = 0;
  private advisoryAlertHeight = 0;
  private inspireSubNavWrapperHeight = 0;
  private inspireSubNavContainer: HTMLElement;
  private setInfoHeaderContainer: HTMLElement;
  private originalScrollHandler: any = null;
  private isDirectiveActive = false;

  constructor(
    private renderer: Renderer2,
    private appConfig: ApplicationConfig,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    const infoSubNavContainer =
      document.querySelector('.information-sub-nav-container') !== null;

    const inspirationSubNavContainer =
      document.querySelector('.inspiration-sub-nav-container') !== null;

    if (
      this.checkStaticPages() &&
      (infoSubNavContainer || inspirationSubNavContainer) &&
      isPlatformBrowser(this.platformId)
    ) {
      this.calculateHeights();
      setTimeout(() => {
        this.setComponentPositions();
        this.overrideExistingScrollHandlers();
      }, 200);
    }
  }

  ngOnDestroy(): void {
    // Restore original scroll handler if we replaced it
    if (this.originalScrollHandler && isPlatformBrowser(this.platformId)) {
      window.onscroll = this.originalScrollHandler;
    }
  }

  private overrideExistingScrollHandlers(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Store the original scroll handler if it exists
      this.originalScrollHandler = window.onscroll;

      // Override the window.onscroll handler
      window.onscroll = (event) => {
        // Mark that our directive is handling the scroll
        this.isDirectiveActive = true;

        // Call our scroll handler
        this.handleScrollEvent(event);

        // Optionally call the original handler if needed
        // if (this.originalScrollHandler) {
        //   this.originalScrollHandler(event);
        // }

        // Reset the flag
        this.isDirectiveActive = false;
      };
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event): void {
    // Only handle the event if it wasn't triggered by our override
    if (!this.isDirectiveActive) {
      this.handleScrollEvent(event);
    }
  }

  private handleScrollEvent(event: Event): void {
    if (
      isPlatformBrowser(this.platformId) &&
      document.querySelector('.inspiration-sub-nav-container') === null
    ) {
      this.setDeltaLogoPosition('0');
      return;
    }

    // Close inspiration sub-nav when scrolling
    // For window:scroll events, event.target is window, not an Element
    // Only check classList for Element targets
    const isWindowScrollEvent = event.target === window;
    const isElementWithRelevantClass =
      event.target instanceof Element &&
      (event.target.classList.contains('panel-body') ||
        event.target.classList.contains('inspiration-sub-nav-link') ||
        event.target.classList.contains('nav-item'));

    if (isWindowScrollEvent || !isElementWithRelevantClass) {
      const inspirationSubNavLinks = document.querySelectorAll(
        '.inspiration-sub-nav-link-container'
      );
      inspirationSubNavLinks.forEach((element) => {
        element.classList.remove('show');
      });

      const expandedLinks = document.querySelectorAll(
        '.inspiration-sub-nav-link'
      );
      expandedLinks.forEach((element) => {
        element.setAttribute('aria-expanded', 'false');
      });
    }

    this.calculateHeights();
    this.updatePositions();
  }

  private checkStaticPages(): boolean {
    return (
      this.appConfig.get('APP') &&
      this.appConfig.get('APP') === 'static' &&
      this.appConfig.get('APP_BAU_OR_STATIC_PG') &&
      this.appConfig.get('APP_BAU_OR_STATIC_PG') === 'true'
    );
  }

  private calculateHeights(): void {
    const navbarElement = document.querySelector('.navbar');
    this.stickyHeaderHeight = navbarElement ? navbarElement.clientHeight : 0;

    const alertElement = document.querySelector(
      '.alert-advisory-main-container'
    );
    this.advisoryAlertHeight = alertElement ? alertElement.clientHeight : 0;

    const inspireNavElement = document.querySelector(
      '.inspiration-sub-nav-container'
    );
    this.inspireSubNavWrapperHeight = inspireNavElement
      ? inspireNavElement.clientHeight
      : 0;
  }

  private updatePositions(): void {
    const windowWidth = window.innerWidth;
    const hasAdvisoryAlert =
      document.querySelector('.alert-advisory-main-container') !== null;
    const isScrolledToTop = window.scrollY === 0;

    if (windowWidth <= 991) {
      // Mobile view
      const newStickyPosition = hasAdvisoryAlert
        ? `${(this.stickyHeaderHeight + this.advisoryAlertHeight) / 16}rem`
        : `${this.stickyHeaderHeight / 16}rem`;

      this.setInspireSubNavPosition(newStickyPosition);

      const logoNewPosition = hasAdvisoryAlert
        ? `${
            (this.stickyHeaderHeight +
              this.advisoryAlertHeight +
              this.inspireSubNavWrapperHeight) /
            16
          }rem`
        : `${
            (this.stickyHeaderHeight + this.inspireSubNavWrapperHeight) / 16
          }rem`;

      this.setDeltaLogoPosition(logoNewPosition);
    } else {
      // Desktop view
      const newStickyPosition = hasAdvisoryAlert
        ? `${(this.stickyHeaderHeight + this.advisoryAlertHeight) / 16}rem`
        : `${this.stickyHeaderHeight / 16}rem`;

      this.setInspireSubNavPosition(newStickyPosition);

      if (isScrolledToTop && !hasAdvisoryAlert) {
        // Fixed value of 8.8rem when at the top and no advisory
        this.setDeltaLogoPosition('8.8rem');
      } else {
        const logoNewPosition = hasAdvisoryAlert
          ? `${
              (this.advisoryAlertHeight + this.inspireSubNavWrapperHeight) / 16
            }rem`
          : `${this.inspireSubNavWrapperHeight / 16}rem`;

        this.setDeltaLogoPosition(logoNewPosition);
      }
    }
  }

  private setInspireSubNavPosition(position: string): void {
    this.inspireSubNavContainer = document.querySelector(
      '.inspiration-sub-nav-container'
    );
    this.setInfoHeaderContainer = document.querySelector(
      '.information-sub-nav-container'
    );
    if (this.inspireSubNavContainer) {
      this.renderer.setStyle(
        this.inspireSubNavContainer,
        'margin-top',
        position
      );
    }

    if (this.setInfoHeaderContainer) {
      this.renderer.setStyle(
        this.setInfoHeaderContainer,
        'margin-top',
        position
      );
    }
  }

  private setDeltaLogoPosition(position: string): void {
    const deltaLogoContainer = document.querySelector('.delta-logo-container');
    if (deltaLogoContainer) {
      this.renderer.setStyle(deltaLogoContainer, 'margin-top', position);
    }
  }

  private setComponentPositions(): void {
    // set margin top to inspiration sub nav dynamically
    const navbar = document.querySelector('.navbar') as HTMLElement;
    const headerHeight = navbar ? navbar.offsetHeight : 0;

    const alertAdvisory = document.querySelector(
      '.alert-advisory-main-container'
    ) as HTMLElement;
    const advisoryAlertHeight = alertAdvisory ? alertAdvisory.offsetHeight : 0;

    const inspireSubNav = document.querySelector(
      '.inspiration-sub-nav-container.fixed-top'
    ) as HTMLElement;
    const inspireSubNavWrapperHeight = inspireSubNav
      ? inspireSubNav.offsetHeight
      : 0;

    // if advisory present add header and advisory height else only add header height
    if (window.innerWidth <= 991) {
      // for mobile, header becomes navbar
      if (!alertAdvisory) {
        const headerWithoutAdvisory = `${headerHeight / 16}rem`;
        const mobileLogoPosition = `${
          (inspireSubNavWrapperHeight + headerHeight) / 16
        }rem`;

        const deltaLogoMobileMargin = this.inspireSubNavContainer
          ? mobileLogoPosition
          : '0rem';

        this.setInspireSubNavPosition(headerWithoutAdvisory);
        this.setDeltaLogoPosition(deltaLogoMobileMargin);
      } else {
        const headerWithAdvisory = `${
          (headerHeight + advisoryAlertHeight) / 16
        }rem`;
        const mobileLogoPosition = `${
          (inspireSubNavWrapperHeight + headerHeight + advisoryAlertHeight) / 16
        }rem`;

        this.setInspireSubNavPosition(headerWithAdvisory);
        this.setDeltaLogoPosition(mobileLogoPosition);
      }
    } else {
      if (!alertAdvisory) {
        const headerWithoutAdvisory = `${headerHeight / 16}rem`;

        this.setInspireSubNavPosition(headerWithoutAdvisory);
        const deltaLogoMargin = this.inspireSubNavContainer ? '8.8rem' : '0';
        this.setDeltaLogoPosition(deltaLogoMargin); // Fixed value of 8.8rem when no advisory
      } else {
        const headerWithAdvisory = `${
          (headerHeight + advisoryAlertHeight) / 16
        }rem`;
        const desktopLogoPosition = `${
          (inspireSubNavWrapperHeight + advisoryAlertHeight) / 16
        }rem`;

        this.setInspireSubNavPosition(headerWithAdvisory);
        this.setDeltaLogoPosition(desktopLogoPosition);
      }
    }
  }
}
